import React from 'react';
import { Redirect, useLocation } from 'react-router-dom';
import redirectsJson from './globalRedirect.json';
import PrerenderMetaRedirect, {
  RedirectStatusCode
} from 'components/Prerender/PrerenderMetaRedirect';
import isPrerenderCrawler from 'helpers/isPrerenderCrawler';

const redirects = redirectsJson as Record<
  string,
  { to: string; status: RedirectStatusCode }
>;

/**
 * GlobalRedirectWrapper performs fast, static global redirects based on the current pathname.
 *
 * This component checks if the current route matches a redirect rule from globalRedirect.json.
 * If a match is found, it immediately redirects (prerender or client-side), skipping rendering of children.
 * If no match is found, it renders the children as usual.
 *
 * Note: This is intended for static, hardcoded redirects only. It does not support dynamic redirect logic.
 *
 * @param {object} props - The component props.
 * @param {React.ReactNode} props.children - The child components to render if no redirect is needed.
 * @returns {JSX.Element} The redirect component or the children.
 */
const GlobalRedirectWrapper: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  const { pathname } = useLocation();
  const redirect = redirects[pathname];
  const isPrerender = isPrerenderCrawler();

  if (!redirect) return <>{children}</>;

  const { to, status } = redirect;
  return isPrerender && status ? (
    <PrerenderMetaRedirect status={status} to={to} />
  ) : (
    <Redirect to={to} />
  );
};

export default GlobalRedirectWrapper;
